import React, { useState, useEffect } from 'react';
import {
  Copy, Check, Users, TrendingUp, Download,
  Calendar, DollarSign, Award, ChevronDown,
  ChevronUp, FileText, Eye, BarChart3,
  UserCheck, UserPlus, AlertCircle, Link2, Code
} from 'lucide-react';
import { referralService } from '../../services/api/referral.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

export default function ReferralProgram() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // State for referral data
  const [referralStatus, setReferralStatus] = useState<any>(null);
  const [myReferrals, setMyReferrals] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [monthlyReport, setMonthlyReport] = useState<any[]>([]);
  const [yearlyReport, setYearlyReport] = useState<any>(null);
  const [networkStats, setNetworkStats] = useState<any>(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState<'overview' | 'referrals' | 'reports' | 'network'>('overview');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showCustomCodeModal, setShowCustomCodeModal] = useState(false);
  const [showApplyCodeModal, setShowApplyCodeModal] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [applyCode, setApplyCode] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [exportFormat, setExportFormat] = useState<'CSV' | 'PDF' | 'EXCEL' | 'JSON'>('CSV');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // Fetch initial data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [status, referrals, summaryData] = await Promise.all([
        referralService.getMyStatus(),
        referralService.getMyReferrals().catch(() => []),
        referralService.getMySummary().catch(() => null)
      ]);

      setReferralStatus(status);
      setMyReferrals(referrals);
      setSummary(summaryData);

      // If user is not a referrer, show activation modal
      if (!status.isReferrer) {
        setShowActivateModal(true);
      } else {
        // Fetch additional reports if user is a referrer
        fetchReports();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading referral data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const [monthly, yearly, network] = await Promise.all([
        referralService.getMyMonthlyReport(selectedYear).catch(() => []),
        referralService.getMyYearlyReport(selectedYear).catch(() => null),
        referralService.getMyNetwork().catch(() => null)
      ]);
      
      setMonthlyReport(monthly);
      setYearlyReport(yearly);
      setNetworkStats(network);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const handleCopyLink = async () => {
    if (!referralStatus?.referralCode) return;
    try {
      const referralLink = `https://banking.belobaba.io/${referralStatus.referralCode}`;
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleCopyCode = async () => {
    if (!referralStatus?.referralCode) return;
    try {
      await navigator.clipboard.writeText(referralStatus.referralCode);
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy code');
    }
  };

  const handleActivateReferrer = async () => {
    try {
      const res = await referralService.activateAsReferrer();
      if (res.success) {
        toast.success('Successfully activated as referrer!');
        setShowActivateModal(false);
        await fetchAllData();
      } else {
        toast.error(res.message || 'Failed to activate');
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Error activating referrer');
    }
  };

  const handleCreateCustomCode = async () => {
    if (!customCode.trim()) {
      toast.error('Please enter a custom code');
      return;
    }
    
    try {
      const res = await referralService.createCustomCode(customCode);
      if (res.success) {
        toast.success('Custom code created successfully!');
        setShowCustomCodeModal(false);
        setCustomCode('');
        await fetchAllData();
      } else {
        toast.error(res.message || 'Failed to create custom code');
      }
    } catch (error) {
      console.error('Custom code error:', error);
      toast.error('Error creating custom code');
    }
  };

  const handleApplyReferralCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    try {
      const res = await referralService.applyReferralCode(applyCode);
      if (res.success) {
        toast.success('Referral code applied successfully!');
        setShowApplyCodeModal(false);
        setApplyCode('');
        await fetchAllData();
      } else {
        toast.error(res.message || 'Failed to apply code');
      }
    } catch (error) {
      console.error('Apply code error:', error);
      toast.error('Error applying referral code');
    }
  };

  const handleExport = async () => {
    try {
      const exportData = {
        format: exportFormat,
        dateFrom: dateRange.from,
        dateTo: dateRange.to
      };
      
      const res = await referralService.exportMyReport(exportData);
      if (res.fileUrl) {
        const filename = `referral-report-${Date.now()}.${exportFormat.toLowerCase()}`;
        await referralService.downloadExportedFile(res.fileUrl, filename);
        toast.success(`Report exported as ${exportFormat}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error exporting report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Referral Program</h1>
        <p className="text-gray-600">Earn rewards by referring new users to BELOBABA</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Referrals</p>
              <p className="text-2xl font-bold">{myReferrals?.length || 0}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-[#B3A369]">${summary?.totalEarnings || 0}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Commission Rate</p>
              <p className="text-2xl font-bold">{referralStatus?.referralPercentage || 0.5}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Referrals</p>
              <p className="text-2xl font-bold">{myReferrals?.filter(r => r.status === 'ACTIVE')?.length || 0}</p>
            </div>
            <UserCheck className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Referral Code Section */}
      {referralStatus?.isReferrer && (
        <div className="bg-white rounded-xl p-8 mb-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Your Referral Code</h2>
          <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <span className="text-3xl font-mono font-bold flex-1 text-gray-900">
              {referralStatus.referralCode}
            </span>
            <button
              onClick={handleCopyCode}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700 group relative"
              title="Copy referral code"
            >
              {copiedCode ? <Check className="w-6 h-6 text-green-600" /> : <Code className="w-6 h-6" />}
              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Copy code
              </span>
            </button>
            <button
              onClick={handleCopyLink}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700 group relative"
              title="Copy referral link"
            >
              {copied ? <Check className="w-6 h-6 text-green-600" /> : <Link2 className="w-6 h-6" />}
              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Copy link
              </span>
            </button>
          </div>
          <div className="mt-4 flex gap-4">
            <button
              onClick={() => setShowCustomCodeModal(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Create Custom Code
            </button>
            {!referralStatus.referredBy && (
              <button
                onClick={() => setShowApplyCodeModal(true)}
                className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Apply Referral Code
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-4 border-b">
          {['overview', 'referrals', 'reports', 'network'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-4 py-2 font-semibold capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-xl font-bold mb-4">Program Benefits</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-green-500 mt-1" />
                <p>Earn 0.5% commission on all transactions from your referrals</p>
              </div>
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-blue-500 mt-1" />
                <p>For every 100 completed onboardings, increase your rate by 0.5%</p>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-purple-500 mt-1" />
                <p>No limit on the number of users you can refer</p>
              </div>
            </div>
            
            {monthlyReport.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">Monthly Earnings Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyReport}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="earnings" stroke="#3B82F6" name="Earnings ($)" />
                    <Line type="monotone" dataKey="referrals" stroke="#10B981" name="New Referrals" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Your Referrals</h3>
              <span className="text-sm text-gray-600">
                Total: {myReferrals.length} users
              </span>
            </div>
            
            {myReferrals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Registered</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-right py-3 px-4">Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myReferrals.map((referral, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{referral.name}</td>
                        <td className="py-3 px-4">{referral.email}</td>
                        <td className="py-3 px-4">
                          {new Date(referral.registeredAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            referral.status === 'ACTIVE' 
                              ? 'bg-green-100 text-green-800'
                              : referral.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {referral.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          ${referral.earnings || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No referrals yet. Share your code to start earning!</p>
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Reports & Analytics</h3>
              <div className="flex gap-4">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as any)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-4 py-2 border rounded-lg"
                >
                  {[2023, 2024, 2025].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {yearlyReport && (
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Current Year Total</p>
                    <p className="text-2xl font-bold text-[#B3A369]">${yearlyReport.totalEarnings}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Previous Year</p>
                    <p className="text-2xl font-bold text-[#B3A369]">${yearlyReport.comparison?.previousTotal || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Growth</p>
                    <p className="text-2xl font-bold">
                      {yearlyReport.comparison?.growth > 0 ? '+' : ''}
                      {yearlyReport.comparison?.growth?.toFixed(2) || 0}%
                    </p>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={yearlyReport.monthlyBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="earnings" fill="#3B82F6" name="Earnings ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Export Section */}
            <div className="border-t pt-6">
              <h4 className="font-semibold mb-4">Export Reports</h4>
              <div className="flex flex-wrap gap-4">
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                  className="px-4 py-2 border rounded-lg"
                />
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                  className="px-4 py-2 border rounded-lg"
                />
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as any)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="CSV">CSV</option>
                  <option value="PDF">PDF</option>
                  <option value="EXCEL">Excel</option>
                  <option value="JSON">JSON</option>
                </select>
                <button
                  onClick={handleExport}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Network Tab */}
        {activeTab === 'network' && (
          <div>
            <h3 className="text-xl font-bold mb-6">Network Statistics</h3>
            {networkStats ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Network</p>
                    <p className="text-2xl font-bold">{networkStats.totalNetwork}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Active Users</p>
                    <p className="text-2xl font-bold">{networkStats.activeUsers}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Transactions</p>
                    <p className="text-2xl font-bold">{networkStats.totalTransactions}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Network Earnings</p>
                    <p className="text-2xl font-bold text-[#B3A369]">${networkStats.networkEarnings}</p>
                  </div>
                </div>

                {networkStats.topPerformers && networkStats.topPerformers.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-4">Top Performers</h4>
                    <div className="space-y-2">
                      {networkStats.topPerformers.map((performer: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{performer.name}</span>
                          <span className="font-bold text-[#B3A369]">${performer.earnings}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Network statistics will appear here</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {/* Activate Modal */}
      {showActivateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Become a Referrer</h2>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-1" />
                <p className="text-sm">Earn 0.5% commission on all referral transactions</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-1" />
                <p className="text-sm">Increase your rate by 0.5% for every 100 onboardings</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-1" />
                <p className="text-sm">No limits on referrals</p>
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowActivateModal(false);
                  navigate('/dashboard');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleActivateReferrer}
                className="flex-1 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
              >
                Activate Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Code Modal */}
      {showCustomCodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Create Custom Code</h2>
            <p className="text-gray-600 mb-4">Choose a memorable code for your referrals (FREE)</p>
            <input
              type="text"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              placeholder="Enter custom code"
              className="w-full px-4 py-2 border rounded-lg mb-6"
              maxLength={20}
            />
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowCustomCodeModal(false);
                  setCustomCode('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCustomCode}
                className="flex-1 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
              >
                Create Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Code Modal */}
      {showApplyCodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Apply Referral Code</h2>
            <p className="text-gray-600 mb-4">Enter the referral code from who invited you</p>
            <input
              type="text"
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="w-full px-4 py-2 border rounded-lg mb-6"
            />
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowApplyCodeModal(false);
                  setApplyCode('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyReferralCode}
                className="flex-1 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
              >
                Apply Code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}